import cx from 'classnames'
import {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { separatorReveal, slideReveal } from 'src/hooks/useAppear'
import { colors } from 'src/theme'

import KeyFigure from '../KeyFigure'

import * as SC from './styled'

export type FigureCardProps = MinimalSeoBlock & {
  className?: string
  data: string | string[]
  complement: string
  color?: string
  complementHeight?: number
  index?: number
}

const FigureCard: ForwardRefExoticComponent<
  FigureCardProps & RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, FigureCardProps>(
  (
    {
      className,
      data,
      complement,
      htmlTag,
      color = colors.yellowPale,
      complementHeight,
      index = 0,
    },
    ref
  ) => {
    const [hasAppeared, setHasAppered] = useState(false)

    const handleAnimationStart = useCallback(() => {
      setHasAppered(true)
    }, [])

    const renderData = useMemo(() => {
      if (typeof data === 'string') {
        return (
          <SC.Datum>
            <KeyFigure
              value={data ?? ''}
              delay={index * 0.12}
              withMotion={hasAppeared}
            />
          </SC.Datum>
        )
      }

      return data.map(
        (datum, datumIndex) =>
          datum && (
            <SC.Datum key={`datum-${datumIndex}`}>
              <KeyFigure
                value={datum ?? ''}
                delay={index * 0.12}
                withMotion={hasAppeared}
              />
            </SC.Datum>
          )
      )
    }, [data, index, hasAppeared])

    const { variants } = useFigureCardMotion()

    return (
      <SC.Container
        ref={ref}
        as={htmlTag}
        className={cx('FigureCard', className)}
        data-height-parent
        $color={color}
      >
        <SC.Data
          variants={variants.data}
          onAnimationStart={handleAnimationStart}
        >
          {renderData}
        </SC.Data>
        <SC._Separator
          aria-hidden
          role="separator"
          variants={variants.separator}
        />
        <SC.Complement data-height $height={complementHeight}>
          <SC.ComplementContent data-height-ref variants={variants.complement}>
            {complement}
          </SC.ComplementContent>
        </SC.Complement>
      </SC.Container>
    )
  }
)

FigureCard.displayName = 'FigureCard'

const useFigureCardMotion = () => {
  const variants = useMemo(
    () => ({
      data: slideReveal({ delay: 0.1 }),
      separator: separatorReveal({ duration: 1.3 }),
      complement: slideReveal({ y: '100%', delay: 0.24 }),
    }),
    []
  )

  return { variants }
}

export default FigureCard
